import { BackArrow } from "../Icons/Icons"
import Dropdown from "../Dropdown/Dropdown"
import Input from "../Input/Input"
import Calendar from "../Calendar/Calendar"
import DateTimeField from "../Calendar/DateTimeField"
import React, { useEffect, useState } from "react"
import Button from "../Button/Button"
import TextArea from "../TextArea/TextArea"
import { showSnackBar } from "../Snackbar/Snackbar"
import { useDispatch } from "react-redux"
import { createTaskReq, getTaskPeopleReq, getTaskTemplatesReq } from "../../features/tasks/tasks"
import { clearListCache } from "../../utils/listCache"
import { useMyContextLoading } from "../LoadingContextProvider/LoadingContextProvider"
import { getAssignToInfo } from "../../features/customer/customerSlice"
import AutoComplete from "../AutoComplete/AutoComplete"
import { now, dateToServer, dateTimeToServer, isRealDate, userTimeZone } from "../../utils/date"
import { useTaskFlags } from "./useTaskFlags"
import { MAX_REMINDER_RECIPIENTS, NOTIFY_REQUIRED_MESSAGE, RECIPIENT_LIMIT_MESSAGE } from "./taskReminders"

const NO_PEOPLE = []

const AddTask = ({ setActiveTab, userId, isTasksPage }) => {
  const [isError, setIsError] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [dueDate, setDueDate] = useState(now())
  const [assignedTo, setAssignedTo] = useState()
  const [templateData, setTemplateData] = useState([])
  const [template, setTemplate] = useState("")
  const [trigger, setTrigger] = useState(false)
  const [selectedValue, setSelectedValue] = useState('')
  const [usersDropdown, setUsersDropdown] = useState('')
  const [assignedToDropdown, setAssignedToDropdown] = useState('')
  const [usersData, setUsersData] = useState('')
  const [inputValue, setInputValue] = useState('')
  const [remindAt, setRemindAt] = useState(null)
  const [recipients, setRecipients] = useState(null)
  const [people, setPeople] = useState(NO_PEOPLE)

  const dispatch = useDispatch()
  const flags = useTaskFlags()
  const { fixesOn, viewer, viewerReady } = flags
  const peopleMode = flags.peopleMode && !isTasksPage
  const reminderUiOn = flags.reminderUiOn && !isTasksPage
  const reportsFailure = fixesOn || peopleMode
  const { triggerLoading } = useMyContextLoading()

  const notify = recipients ?? (people.some((person) => person.value === viewer?._id) ? [viewer._id] : NO_PEOPLE)

  const saveNewTask = async () => {
    triggerLoading(true)
    try {
      const selectedUserId = peopleMode
        ? undefined
        : usersData?.find((e) => e?.firstName + " " + e?.lastName === selectedValue)?._id
      if (!title || !description || !dueDate || !assignedTo) {
        setIsError(true)
        showSnackBar("error", `${!title ? "Title" : ''} ${!dueDate ? "Due Date" : ''} ${!assignedTo ? "Assigned To" : ''} are required fields`)
        triggerLoading(false)
        return
      }
      if (isTasksPage && !selectedValue) {
        setIsError(true)
        showSnackBar("error", `${!selectedValue ? "User" : ''} is required fields`)
        triggerLoading(false)
        return
      }
      if (reminderUiOn && remindAt && !isRealDate(remindAt)) {
        setIsError(true)
        showSnackBar("error", "Remind at is not a valid date and time")
        triggerLoading(false)
        return
      }
      if (reminderUiOn && remindAt && notify.length === 0) {
        showSnackBar("error", NOTIFY_REQUIRED_MESSAGE)
        triggerLoading(false)
        return
      }

      const taskData = {
        title,
        description,
        due: dateToServer(dueDate),
        assignedTo,
        userId: isTasksPage ? selectedUserId : userId,
        ...(reminderUiOn && remindAt
          ? { reminder: { at: dateTimeToServer(remindAt), recipients: notify, timeZone: userTimeZone } }
          : {})
      }
      const request = dispatch(createTaskReq(taskData))
      await (reportsFailure ? request.unwrap() : request)
      showSnackBar("success", "Task successfully created")
      clearListCache('tasks')
      setActiveTab("tasks")
    } catch (error) {
      reportsFailure && showSnackBar("error", error)
      console.error(error)
    }
    triggerLoading(false)
  }
  const getTemplates = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getTaskTemplatesReq())
      setTemplateData(response?.payload?.data)
    } catch (error) {
      console.error(error)
    }
    triggerLoading(false)
  }
  const handleDropdown = async (e) => {
    setTemplate(e)
  }

  const handleDropdownAssignedTo = async (e) => {
    setAssignedTo(e)
  }

  const changeNotify = (next) => {
    if (next.length > MAX_REMINDER_RECIPIENTS) {
      showSnackBar("error", RECIPIENT_LIMIT_MESSAGE)
      return
    }
    setRecipients(next)
  }

  const fetchContacts = async () => {
    try {
      const response = await dispatch(getAssignToInfo())

      setUsersDropdown(response?.payload?.data?.map(user => user.firstName + " " + user.lastName))
      setAssignedToDropdown(response?.payload?.data?.length > 0 ? response?.payload?.data?.map(user => ({
        label: user.firstName + " " + user.lastName,
        value: user?._id
      })) : assignedToDropdown)

      setUsersData(response?.payload?.data?.length > 0 ? response?.payload?.data : usersData)
    } catch (error) {
      showSnackBar("error", error)
      console.error('An unexpected error occurred:', error)
    }
  }

  useEffect(() => {
    setIsError(false)
  }, [title, description, dueDate, assignedTo])

  useEffect(() => {
    getTemplates()
  }, [])

  useEffect(() => {
    viewerReady && !peopleMode && fetchContacts()
    inputValue !== selectedValue && setSelectedValue('')
  }, [inputValue, viewerReady])

  useEffect(() => {
    if (!peopleMode) return undefined
    let current = true
    dispatch(getTaskPeopleReq(userId)).then((response) => {
      if (!current) return
      if (response?.error) {
        showSnackBar("error", typeof response.payload === 'string' && response.payload ? response.payload : "Failed to fetch people")
        return
      }
      setPeople((response?.payload?.data || NO_PEOPLE).map(({ value, label }) => ({ value, label })))
    })
    return () => {
      current = false
    }
  }, [peopleMode, userId, dispatch])

  useEffect(() => {
    templateData.map((e) => {
      if (e?.value === template) {
        setDescription(e?.description)
        setTitle(e?.label)
        setTrigger(!trigger)
      }
    })
  }, [template])

  return (
    <div className={`flex px-6 w-full flex-col gap-6 mb-6 ${isTasksPage && "justify-center items-center"}`}>
      <div className="w-full  flex flex-col gap-8">
        {isTasksPage ?
          <div className="flex items-center justify-start gap-2 cursor-pointer" onClick={() => setActiveTab("tasks")}>

            <h5 className="font-bold text-xl">Create New Task</h5>
          </div>
          :
          <div className="flex items-center justify-start gap-2 cursor-pointer" onClick={() => setActiveTab("tasks")}>
            <div className="h-6 w-6 flex items-center justify-center">
              <BackArrow />
            </div>
            <h5 className="font-bold text-xl-20">Create New Task</h5>
          </div>}
      </div>
      <div
        className={`flex flex-col gap-6 px-12 py-12 rounded-2xl shadow-lg border border-divider w-full ${isTasksPage && "max-w-[898px]"} `}>
        <div className="flex gap-4">
          <Dropdown value={template} className="w-full" menuItems={templateData} setValue={handleDropdown}
            label="Template" />
          {isTasksPage &&
            <AutoComplete label="User" className="w-full" options={usersDropdown} setInputValue={setInputValue}
              inputValue={inputValue}
              setSelectedValue={setSelectedValue} selectedValue={selectedValue} />}
        </div>
        <Input errorMsg={isError && !title} placeholder={"Enter title"} label={"Title"} value={title}
          onChange={(e) => setTitle(e.target.value)} />
        <TextArea text={description} setText={setDescription} placeholder="Enter Description" trigger={trigger} />
        <div className="flex items-end gap-4 pb-6">
          <div className="w-full">
            <Calendar
              width="w-full"
              label="Due Date"
              value={dueDate}
              onChange={(date) => setDueDate(date)}
            />
          </div>
          <div className="w-full">
            <Dropdown label="Assign To" placeholder="Select a Value"
              menuItems={peopleMode ? people : (assignedToDropdown || [])} menuFitsField={peopleMode}
              value={assignedTo} setValue={handleDropdownAssignedTo} />
          </div>
        </div>
        {reminderUiOn && (
          <div className="flex items-end gap-4 pb-6">
            <div className="w-full">
              <DateTimeField width="w-full" label="Remind at" value={remindAt} onChange={setRemindAt} />
            </div>
            <div className="w-full">
              <Dropdown label="Notify" ariaLabel="Notify" placeholder="Select people" isMulti menuFitsField
                helperText={`Up to ${MAX_REMINDER_RECIPIENTS} people`}
                menuItems={people} value={people.length ? notify : NO_PEOPLE} setValue={changeNotify} />
            </div>
          </div>
        )}
        <div className="flex justify-end items-center gap-4 self-stretch py-6 px-6 border-t border-divider ">
          <Button onClick={() => setActiveTab("tasks")} type="outlined" size="lg" width="w-[156px]">Cancel</Button>
          <Button size="lg" width="w-[156px]" onClick={saveNewTask}>Save</Button>
        </div>
      </div>
    </div>
  )
}
export default AddTask